import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowLeft02Icon, ArrowRight02Icon, FloppyDiskIcon } from '@hugeicons-pro/core-twotone-rounded/index'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Box, Button, Divider, Typography } from '@mui/material'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import {
    useCreateCustomerMutation,
    useGetContactQuery,
    useGetCustomerQuery,
    useUpdateCustomerMutation,
} from '@/app/services/customer'
import { useCreateStripeCheckoutSessionMutation } from '@/app/services/integrations'
import { useGetOrderItemsQuery } from '@/app/services/orderItems'
import { useConfirmOrderMutation, useGetOrderQuery, useUpdateOrderMutation } from '@/app/services/orders'
import { useGetStoreSettingsQuery } from '@/app/services/organisation'
import { selectContactId } from '@/app/slices/contactsSlice'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { selectGuestUser, selectIsGuestLoggedIn, setGuestUserData } from '@/app/slices/guestUserSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { TbxTooltip } from '@/common/components'
import { formatCurrency, formatNumber } from '@/common/helpers/formatUtilities'
import { getLargestDimensions, ORDER_STATUS_ARRAY, ORDER_USER_TYPE, Paths } from '@/common/utils'

dayjs.extend(isSameOrBefore)

const classes = {
    orderSummary: {
        padding: 3,
        gap: 3,
        minHeight: {
            lg: 'calc(100vh - 114px)',
        },
    },
    summaryTitle: {
        fontWeight: 700,
        textTransform: 'uppercase',
    },
    issueMessages: {
        gap: 3,
    },
    pricingSummary: {
        gap: 1.5,
    },
    checkoutButton: {
        gap: 1,
    },
    saveOrder: {
        gap: 1,
    },

    needHelp: {
        maxWidth: '500px',
    },
}

const OrderCheckoutSummary = ({ form }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const dispatch = useDispatch()
    const { orderId } = useParams()
    const { isAuthenticated } = useKindeAuth()

    const {
        formState: { isDirty },
        handleSubmit,
    } = form

    const organisationId = useSelector(selectOrganisationId)
    const currentCustomerId = useSelector(selectCustomerId)
    const currentContactId = useSelector(selectContactId)
    const isGuestLoggedIn = useSelector(selectIsGuestLoggedIn)
    const guestUser = useSelector(selectGuestUser)

    const { data: storeSettings } = useGetStoreSettingsQuery({
        organisationId,
    })

    const { data: order } = useGetOrderQuery({
        organisationId,
        orderId,
    })

    const { data: orderItems } = useGetOrderItemsQuery({
        organisationId,
        orderId,
    })

    const { data: currentCustomer } = useGetCustomerQuery(
        { organisationId, customerId: currentCustomerId },
        { skip: !isAuthenticated && !currentCustomerId }
    )

    const { data: currentContact } = useGetContactQuery(
        {
            organisationId,
            customerId: currentCustomerId,
            contactId: currentContactId,
        },
        { skip: !isAuthenticated && !currentCustomerId && !currentContactId }
    )

    const isQuoted = useMemo(() => order?.status === ORDER_STATUS_ARRAY.Issued, [order])

    const prices = useMemo(() => {
        return {
            parts: order?.processingTotalPrice + order?.materialPrice,
            secondaryProcesses: order?.foldingTotalPrice + order?.secondaryProcessTotalPrice,
            miscellaneous: order?.miscellaneousItemsTotalPrice,
            adjustments: order?.otherChargeTotalPrice,
            minimumChargePrice: order?.minimumQuoteChargePrice,
            subtotal: order?.linePrice + (order?.minimumQuoteChargePrice || 0) - (order?.deliveryPrice || 0),
            delivery: order.deliveryPrice,
            taxes: (order.linePrice + (order.minimumQuoteChargePrice || 0)) * order.taxRate,
            total: order.lineTaxedPrice,
        }
    }, [order])

    const [createCustomer] = useCreateCustomerMutation()
    const [updateCustomer] = useUpdateCustomerMutation()
    const [updateOrder] = useUpdateOrderMutation()
    const [confirmOrderMutation] = useConfirmOrderMutation()
    const [createStripeCheckoutSession] = useCreateStripeCheckoutSessionMutation()

    const [isPreparingOrder, setIsPreparingOrder] = useState(false)
    const [isInitiatingPayment, setIsInitiatingPayment] = useState(false)
    const [shippingAndTaxCalculated, setShippingAndTaxCalculated] = useState(isQuoted)

    const largestDimensions = getLargestDimensions(orderItems, storeSettings)

    const paymentsEnabled = storeSettings?.paymentsEnabledWebStore
    const deliveryOptionSelected = form.getValues('shippingOption')

    const showDeliveryPrice = deliveryOptionSelected === 'Delivery' && order?.deliveryPricingMethod === 'ChargeToOrder'

    const updateOrderContact = useCallback(
        (
            contactId,
            userType,
            shippingOption,
            customerId,
            deliveryProviderToUse,
            customerDeliveryProvider,
            customerDeliveryAccountNumber,
            billingAddressId,
            deliveryAddressId,
            requiredShippingDate
        ) => {
            return updateOrder({
                orderId: order.id,
                organisationId: order.organisationId,
                order: {
                    name: order.name,
                    quoteId: order.id,
                    customerContactId: contactId,
                    quoteUserType: userType,
                    deliveryOption: shippingOption,
                    customerId: customerId,
                    deliveryPricingMethod: order.deliveryPricingMethod,
                    ...(deliveryProviderToUse === 'customerProvider' && {
                        customerDeliveryProvider,
                        customerDeliveryAccountNumber,
                    }),
                    billingAddressId: billingAddressId,
                    deliveryAddressId: deliveryAddressId,
                    requiredDateUtc: requiredShippingDate,
                },
            })
        },
        [order, updateOrder]
    )

    const updateOrderStatus = useCallback(
        (confirmedOrder, orderStatus, contactId, userType, shippingOption, customerId, requiredShippingDate) => {
            return updateOrder({
                orderId: confirmedOrder.id,
                organisationId: confirmedOrder.organisationId,
                order: {
                    name: confirmedOrder.name,
                    quoteId: confirmedOrder.id,
                    taxRateId: confirmedOrder.taxRateId,
                    taxRate: confirmedOrder.taxRate,
                    customerId: customerId,
                    status: orderStatus,
                    customerContactId: contactId,
                    quoteUserType: userType,
                    deliveryOption: shippingOption,
                    deliveryPricingMethod: order.deliveryPricingMethod,
                    deliveryAddressId: order.deliveryAddressId,
                    billingAddressId: order.billingAddressId,
                    requiredDateUtc: requiredShippingDate,
                },
            })
        },
        [order, updateOrder]
    )

    const getAddress = (address, type) => ({
        addressType: type,
        line1: address.line1,
        line2: address.line2,
        city: address.city,
        state: address.state,
        country: address.country,
        postcode: address.postcode,
    })

    const createCustomerAndContact = (data) => {
        const {
            billingAddress,
            billingCountryCode,
            companyName,
            deliveryAddress,
            deliveryCountryCode,
            email,
            name,
            phone,
            useOneAddress,
        } = data
        const { organisationId } = order

        const hasDeliveryAddress = Object.values(deliveryAddress).some((field) => Boolean(field))

        const resolvedDeliveryAddress =
            useOneAddress || !hasDeliveryAddress
                ? getAddress(billingAddress, 'Delivery')
                : getAddress(deliveryAddress, 'Delivery')

        return createCustomer({
            organisationId,
            customer: {
                companyName,
                useOneAddress: useOneAddress,
                contacts: [{ name, email, phone }],
                billingAddress: getAddress(billingAddress, 'Billing'),
                deliveryAddress: resolvedDeliveryAddress,
                deliveryCountryCode,
                billingCountryCode,
            },
        })
    }

    const updateCustomerAndContact = (data) => {
        const {
            billingAddress,
            billingCountryCode,
            companyName,
            deliveryAddress,
            deliveryCountryCode,
            name,
            phone,
            useOneAddress,
        } = data
        const { organisationId } = order

        const customer = isAuthenticated ? currentCustomer : guestUser
        const contact = isAuthenticated ? currentContact : guestUser

        const emailAddress = isAuthenticated ? contact.email : guestUser.email
        const company = companyName ? companyName : name

        const resolvedDeliveryAddress = useOneAddress
            ? getAddress(billingAddress, 'Delivery')
            : getAddress(deliveryAddress, 'Delivery')

        return updateCustomer({
            organisationId,
            customerId: customer.customerId,
            customer: {
                customerId: customer.customerId,
                companyName: company,
                useOneAddress: useOneAddress,
                contacts: [{ contactId: contact.contactId, name, email: emailAddress, phone }],
                billingAddress: getAddress(billingAddress, 'Billing'),
                deliveryAddress: resolvedDeliveryAddress,
                deliveryCountryCode,
                billingCountryCode,
            },
        })
    }

    const preparingOrder = async (data) => {
        const {
            customerDeliveryAccountNumber,
            customerDeliveryProvider,
            deliveryProviderToUse,
            requiredShippingDate,
            shippingOption,
        } = form.getValues()
        let customerId, contactId, email, orderUserType

        try {
            if (!isAuthenticated && !guestUser?.customerId) {
                const result = await createCustomerAndContact(data).unwrap()

                if (result?.contacts?.[0]?.contactId) {
                    await updateOrderContact(
                        result.contacts[0].contactId,
                        ORDER_USER_TYPE.Guest,
                        shippingOption,
                        result.customerId,
                        deliveryProviderToUse,
                        customerDeliveryProvider,
                        customerDeliveryAccountNumber,
                        result?.billingAddress?.addressId,
                        result?.deliveryAddress?.addressId,
                        requiredShippingDate
                    ).unwrap()
                }

                customerId = result.customerId
                contactId = result.contacts[0].contactId
                orderUserType = ORDER_USER_TYPE.Guest

                dispatch(setGuestUserData({ ...data, customerId, contactId }))
            }

            if (isAuthenticated && currentCustomer) {
                const result = await updateCustomerAndContact(data).unwrap()

                await updateOrderContact(
                    currentCustomer.contactId,
                    ORDER_USER_TYPE.Customer,
                    shippingOption,
                    currentCustomer.customerId,
                    deliveryProviderToUse,
                    customerDeliveryProvider,
                    customerDeliveryAccountNumber,
                    result?.billingAddress?.addressId,
                    result?.deliveryAddress?.addressId,
                    requiredShippingDate
                ).unwrap()

                customerId = currentCustomer.customerId
                contactId = currentCustomer.contactId
                orderUserType = ORDER_USER_TYPE.Customer
            }

            if (isGuestLoggedIn && guestUser?.customerId) {
                const result = await updateCustomerAndContact(data).unwrap()

                await updateOrderContact(
                    guestUser.contactId,
                    ORDER_USER_TYPE.Guest,
                    shippingOption,
                    guestUser.customerId,
                    deliveryProviderToUse,
                    customerDeliveryProvider,
                    customerDeliveryAccountNumber,
                    result?.billingAddress?.addressId,
                    result?.deliveryAddress?.addressId,
                    requiredShippingDate
                ).unwrap()

                customerId = guestUser.customerId
                contactId = guestUser.contactId
                email = guestUser.email
                orderUserType = ORDER_USER_TYPE.Guest

                dispatch(setGuestUserData({ ...data, customerId, contactId, email }))
            }

            return { customerId, contactId, orderUserType }
        } catch (error) {
            throw new Error('Preparing Order Error', { cause: error })
        }
    }

    const confirmOrder = async () => {
        try {
            const confirmedOrder = await confirmOrderMutation({ organisationId, orderId }).unwrap()

            return confirmedOrder
        } catch (error) {
            const errorMessage = t(error.data) || t('Sorry, the order could not be placed. Please try again.')
            throw new Error(errorMessage, { cause: error })
        }
    }

    const handleOrderStatusChange = async (order, newOrderStatus) => {
        try {
            await updateOrder({
                organisationId,
                orderId,
                order: {
                    ...order,
                    status: newOrderStatus,
                },
            }).unwrap()
        } catch (_e) {
            const errorMessage = t('Please try again later')
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 5000 })
        }
    }

    const handleCalculateShippingAndTaxes = async (data) => {
        setIsPreparingOrder(true)

        try {
            await preparingOrder(data)
            const confirmedOrder = await confirmOrder()

            await handleOrderStatusChange(confirmedOrder, ORDER_STATUS_ARRAY.Issued)

            enqueueSnackbar(t('Shipping and taxes calculated'), { variant: 'success' })

            setShippingAndTaxCalculated(true)
        } catch (error) {
            enqueueSnackbar(t(error.message), { variant: 'error', autoHideDuration: 5000 })

            setShippingAndTaxCalculated(false)
        }
        setIsPreparingOrder(false)
    }

    const handleCheckoutOrder = async () => {
        setIsInitiatingPayment(true)
        try {
            const stripeCheckoutSessionUrl = await createStripeCheckoutSession({
                organisationId: organisationId,
                orderId: orderId,
                successUrl: `${window.location.origin}/payments/${orderId}/success`,
                cancelUrl: `${window.location.origin}/orders/${orderId}/checkout`,
            }).unwrap()

            enqueueSnackbar('Redirecting to stripe...', {
                variant: 'info',
                onExited: () => {
                    window.open(stripeCheckoutSessionUrl, '_self')
                    setIsInitiatingPayment(false)
                },
            })
        } catch (error) {
            setIsInitiatingPayment(false)
            console.error('WebStore', 'Payment Button Error', error)
            enqueueSnackbar(error.data || t('Sorry, the order could not be placed. Please try again.'), {
                variant: 'error',
                autoHideDuration: 5000,
            })
        }
    }

    const handlePlaceOrder = async () => {
        if (order.expiryDate) {
            const orderExpiryDate = dayjs(order.expiryDate)
            if (order.expiryDate && !orderExpiryDate.isValid()) {
                enqueueSnackbar(t('Order expiry date is invalid. Please recalculate before placing this order.'), {
                    variant: 'error',
                    autoHideDuration: 5000,
                })
                return
            }

            const currentDate = dayjs()
            if (orderExpiryDate && orderExpiryDate.isSameOrBefore(currentDate, 'day')) {
                enqueueSnackbar(t("Order's price is no longer valid. Please recalculate before placing this order."), {
                    variant: 'error',
                    autoHideDuration: 5000,
                })
                return
            }
        }

        try {
            const status = ORDER_STATUS_ARRAY.PendingOrderConfirmation
            await updateOrderStatus(
                order,
                status,
                order.customerContact.contactId,
                order.quoteUserType,
                order.deliveryOption,
                order.customerContact.customerId
            ).unwrap()

            enqueueSnackbar(t('Order placed'), { variant: 'success' })
        } catch (error) {
            enqueueSnackbar(error.data || t('Sorry, the order could not be placed. Please try again.'), {
                variant: 'error',
                autoHideDuration: 5000,
            })
        }
    }

    const handleSubmitError = (_error) => {
        enqueueSnackbar(t('Sorry, the order could not be placed. Please complete the required fields.'), {
            variant: 'error',
        })
    }

    const handleDownloadQuotePdf = () => {
        const documentPath = Paths.DOCUMENTS_PATHNAME.replace(':orderId', orderId)
            .replace(':documentType', 'quote')
            .replace(':action?', 'export')

        navigate(documentPath)
    }

    const navigateBackToOrder = useCallback(async () => {
        handleOrderStatusChange(order, ORDER_STATUS_ARRAY.Draft)
        navigate(`/orders/${orderId}/review`)
    }, [handleOrderStatusChange, navigate, orderId, order])

    useEffect(() => {
        if (isDirty && isQuoted) {
            handleOrderStatusChange(order, ORDER_STATUS_ARRAY.Draft)
            setShippingAndTaxCalculated(false)
        }
    }, [isDirty, isQuoted])

    return (
        <Box
            sx={[
                {
                    display: 'flex',
                    flexDirection: 'column',
                },
                classes.orderSummary,
            ]}
        >
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    sx={classes.summaryTitle}
                    variant="body1"
                >
                    {t('Order summary')}
                </Typography>
            </Box>
            <Box
                sx={[
                    {
                        display: 'flex',
                        flexDirection: 'column',
                    },
                    classes.pricingSummary,
                ]}
            >
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="body2">{t('Net weight')}</Typography>
                    <Typography
                        className="price"
                        variant="strong2"
                    >
                        {formatNumber(order.totalMass, storeSettings?.locale)}{' '}
                        {storeSettings?.defaultDrawingUnits === 'Metric' ? 'kg' : 'lb'}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="body2">{t('Largest long-dimension')}</Typography>
                    <Typography
                        className="price"
                        variant="strong2"
                    >
                        {largestDimensions.long}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="body2">{t('Largest short-dimension')}</Typography>
                    <Typography
                        className="price"
                        variant="strong2"
                    >
                        {largestDimensions.short}
                    </Typography>
                </Box>
                <Divider />

                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="body2">{t('Parts')}</Typography>
                    <Typography
                        className="price"
                        variant="strong2"
                    >
                        {formatCurrency(prices.parts, storeSettings?.currencyCode, storeSettings?.locale)}
                    </Typography>
                </Box>

                {prices.secondaryProcesses ? (
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="body2">{t('Secondary processes')}</Typography>
                        <Typography
                            className="price"
                            variant="strong2"
                        >
                            {formatCurrency(
                                prices.secondaryProcesses,
                                storeSettings?.currencyCode,
                                storeSettings?.locale
                            )}
                        </Typography>
                    </Box>
                ) : null}

                {prices.miscellaneous ? (
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="body2">{t('Miscellaneous items')}</Typography>
                        <Typography
                            className="price"
                            variant="strong2"
                        >
                            {formatCurrency(prices.miscellaneous, storeSettings?.currencyCode, storeSettings?.locale)}
                        </Typography>
                    </Box>
                ) : null}

                {prices.adjustments ? (
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="body2">{t('Adjustments')}</Typography>
                        <Typography
                            className="price"
                            variant="strong2"
                        >
                            {formatCurrency(prices.adjustments, storeSettings?.currencyCode, storeSettings?.locale)}
                        </Typography>
                    </Box>
                ) : null}

                {prices.minimumChargePrice ? (
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="body2">{t('Minimum order charge')}</Typography>
                        <Typography
                            className="price"
                            variant="strong2"
                        >
                            {formatCurrency(
                                prices.minimumChargePrice,
                                storeSettings?.currencyCode,
                                storeSettings?.locale
                            )}
                        </Typography>
                    </Box>
                ) : null}

                {showDeliveryPrice ? (
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="body2">{t('Delivery')}</Typography>
                        <Typography
                            className="price"
                            variant="strong2"
                        >
                            {shippingAndTaxCalculated
                                ? formatCurrency(prices.delivery, storeSettings?.currencyCode, storeSettings?.locale)
                                : String.fromCharCode(8212)}
                        </Typography>
                    </Box>
                ) : null}
                <Divider />
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="body2">{t('Subtotal')}</Typography>
                    <Typography
                        className="price"
                        variant="strong2"
                    >
                        {formatCurrency(prices.subtotal, storeSettings?.currencyCode, storeSettings?.locale)}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="body2">
                        {t('Taxes')} ({shippingAndTaxCalculated ? `${order.taxRate * 100}%` : String.fromCharCode(8212)}
                        )
                    </Typography>
                    <Typography
                        className="price"
                        variant="strong2"
                    >
                        {shippingAndTaxCalculated
                            ? formatCurrency(prices.taxes, storeSettings?.currencyCode, storeSettings?.locale)
                            : String.fromCharCode(8212)}
                    </Typography>
                </Box>
                <Divider />
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="body1">{t('Total')}</Typography>
                    <Typography
                        className="price"
                        variant="strong1"
                    >
                        {shippingAndTaxCalculated
                            ? formatCurrency(prices.total, storeSettings?.currencyCode, storeSettings?.locale)
                            : String.fromCharCode(8212)}
                    </Typography>
                </Box>
            </Box>
            <Box
                sx={[
                    {
                        display: 'flex',
                        flexDirection: 'column',
                    },
                    classes.checkoutButton,
                ]}
            >
                {deliveryOptionSelected === 'pickup' ? (
                    <Typography variant="small">{t('Shipping: Pickup')}</Typography>
                ) : null}

                {deliveryOptionSelected === 'Delivery' && order?.deliveryPricingMethod === 'SeparateCharge' ? (
                    <Typography variant="small">{t('Shipping: Delivery charged separately')}</Typography>
                ) : null}

                {deliveryOptionSelected === 'Delivery' && order?.deliveryPricingMethod === 'PayUponDelivery' ? (
                    <Typography variant="small">{t('Shipping: Delivery payable to delivery provider')}</Typography>
                ) : null}

                {deliveryOptionSelected === 'Delivery' && order?.deliveryPricingMethod === 'ChargeToOrder' ? (
                    <Typography variant="small">{t('Shipping: Delivery included')}</Typography>
                ) : null}

                {!shippingAndTaxCalculated ? (
                    <Button
                        color="primary"
                        disabled={isPreparingOrder}
                        loading={isPreparingOrder}
                        loadingPosition="start"
                        variant="contained"
                        fullWidth
                        onClick={handleSubmit(handleCalculateShippingAndTaxes, handleSubmitError)}
                    >
                        {isPreparingOrder
                            ? t('Please wait...')
                            : showDeliveryPrice
                              ? t('Calculate shipping & tax')
                              : t('Calculate tax')}
                    </Button>
                ) : paymentsEnabled ? (
                    <Button
                        color="primary"
                        disabled={isInitiatingPayment}
                        endIcon={<HugeiconsIcon icon={ArrowRight02Icon} />}
                        loading={isInitiatingPayment}
                        loadingPosition="start"
                        variant="contained"
                        fullWidth
                        onClick={handleSubmit(handleCheckoutOrder, handleSubmitError)}
                    >
                        {isInitiatingPayment ? t('Please wait...') : t('Checkout')}
                    </Button>
                ) : (
                    <Button
                        color="primary"
                        disabled={isInitiatingPayment}
                        endIcon={<HugeiconsIcon icon={ArrowRight02Icon} />}
                        loading={isInitiatingPayment}
                        loadingPosition="end"
                        variant="contained"
                        fullWidth
                        onClick={handleSubmit(handlePlaceOrder, handleSubmitError)}
                    >
                        {isInitiatingPayment ? t('Placing order...') : t('Place order')}
                    </Button>
                )}

                <Button
                    color="primary"
                    disabled={isPreparingOrder || isInitiatingPayment}
                    startIcon={<HugeiconsIcon icon={ArrowLeft02Icon} />}
                    variant="outlined"
                    fullWidth
                    onClick={navigateBackToOrder}
                >
                    {t('Review order')}
                </Button>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    gap: 3,
                }}
            >
                {isAuthenticated || isGuestLoggedIn ? (
                    <Box>
                        <Typography
                            mb={1}
                            variant="small"
                        >
                            {t('Not ready to place your order?')}
                        </Typography>
                        <TbxTooltip
                            title={!isQuoted ? t('Tax needs to be calculated before the PDF can be generated') : ''}
                        >
                            <span>
                                <Button
                                    color="primary"
                                    disabled={!isQuoted}
                                    href={Paths.DOCUMENTS_PATHNAME.replace(':orderId', orderId)
                                        .replace(':documentType', 'quote')
                                        .replace(':action?', 'export')}
                                    startIcon={<HugeiconsIcon icon={FloppyDiskIcon} />}
                                    target="_blank"
                                    variant="outlined"
                                    fullWidth
                                >
                                    {t('Download quote pdf')}
                                </Button>
                            </span>
                        </TbxTooltip>
                    </Box>
                ) : null}
                {storeSettings?.webStoreSupportEmail || storeSettings?.webStoreContactNumber ? (
                    <Box sx={classes.needHelp}>
                        <Alert
                            severity="info"
                            variant="standard"
                        >
                            <AlertTitle>{t('Need help with your files?')}</AlertTitle>
                            {storeSettings?.webStoreSupportEmail && !storeSettings?.webStoreContactNumber ? (
                                <>
                                    {t('Contact us at')}{' '}
                                    <a href={`mailto:${storeSettings?.webStoreSupportEmail}`}>
                                        {storeSettings?.webStoreSupportEmail}
                                    </a>
                                </>
                            ) : null}
                            {!storeSettings?.webStoreSupportEmail && storeSettings?.webStoreContactNumber ? (
                                <>
                                    {t('Call us at')}{' '}
                                    <a href={`tel:${storeSettings?.webStoreContactNumber}`}>
                                        {storeSettings?.webStoreContactNumber}
                                    </a>
                                </>
                            ) : null}
                            {storeSettings?.webStoreSupportEmail && storeSettings?.webStoreContactNumber ? (
                                <>
                                    {t('Contact us at')}{' '}
                                    <a href={`mailto:${storeSettings?.webStoreSupportEmail}`}>
                                        {storeSettings?.webStoreSupportEmail}
                                    </a>{' '}
                                    {t('or call us at')}{' '}
                                    <a href={`tel:${storeSettings?.webStoreContactNumber}`}>
                                        {storeSettings?.webStoreContactNumber}
                                    </a>
                                </>
                            ) : null}
                        </Alert>
                    </Box>
                ) : null}
            </Box>
        </Box>
    )
}

OrderCheckoutSummary.propTypes = {
    form: PropTypes.object.isRequired,
}

export default OrderCheckoutSummary
